import { useMemo, useState } from 'react';
import '../../styles/Attendance.css';

// A month at a glance: which days the firm works, which are weekly offs, and
// which are holidays — with the reason on hover.
//
// This is a PREVIEW OF THE RULES BEING EDITED, not a report. It reads the
// settings object currently in the form, including unsaved changes, which is
// the whole point: turning Saturday off should show you the month you just
// created before you commit to it. It deliberately does NOT call the server's
// month builder (utils/attendance.js#buildMonth) — that one reports what
// actually happened, against SAVED settings, and would contradict the form.
//
// ⚠️ Dates here are built from (year, month, day) integers and formatted by
// hand. Never `new Date('2026-07-31')` — that parses as UTC and lands on the
// 30th for anyone west of Greenwich, which would silently shift every holiday
// by a day. `new Date(y, m, d)` is local and is only ever asked for getDay(),
// so no instant is involved.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
// Monday-first: an Indian practice's week starts on Monday and Sunday is the
// off day, so a Sunday-first grid puts the rest day in the wrong place.
const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const pad = (n) => String(n).padStart(2, '0');
export const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

// Mon=0 … Sun=6, to index the grid above.
const gridDow = (jsDay) => (jsDay + 6) % 7;

// Parse a 'YYYY-MM-DD' key into its parts. One place, so nothing in this file
// is ever tempted to hand the string to `new Date()` directly.
const parseKey = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return { y, m: m - 1, d };
};
const jsDayOf = (key) => {
  const { y, m, d } = parseKey(key);
  return new Date(y, m, d).getDay();
};
const prettyDate = (key) => {
  const { y, m, d } = parseKey(key);
  return `${d} ${MONTHS[m].slice(0, 3)} ${y}`;
};
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const weekdayName = (key) => WEEKDAYS[jsDayOf(key)];

/**
 * @param {object}   settings          the work week + holiday rules
 * @param {object}   [overlay]         { dateKey → { tone, label, detail } } laid
 *                                     OVER the computed day type. This is what
 *                                     makes one component serve three screens:
 *                                     Settings passes nothing, the employee
 *                                     passes their own month, the cockpit passes
 *                                     one person's. The drawing is identical.
 * @param {string}   [ym]              controlled month ('YYYY-MM'); omit to let
 *                                     the calendar own its own navigation.
 * @param {function} [onMonthChange]   called with 'YYYY-MM' when the user pages
 * @param {boolean}  [showHolidayList] the list under the grid (Settings only)
 * @param {node}     [legendExtra]     extra legend chips for overlay tones
 */
function WorkCalendar({
  settings, canWrite, onPickDate, onRemoveHoliday,
  overlay = null, ym = null, onMonthChange = null,
  showHolidayList = true, legendExtra = null,
}) {
  const today = new Date();
  const [selfView, setSelfView] = useState({ y: today.getFullYear(), m: today.getMonth() });
  // Controlled when a `ym` is supplied — the employee and cockpit screens fetch
  // per month, so the month has to live above the calendar there. Uncontrolled
  // in Settings, where nothing is fetched and the calendar can own it.
  // Memoised: the controlled branch builds a fresh object every render, which
  // would invalidate the grid memo below on each one and defeat it entirely.
  const view = useMemo(
    () => (ym ? { y: Number(ym.slice(0, 4)), m: Number(ym.slice(5, 7)) - 1 } : selfView),
    [ym, selfView]
  );
  const setView = (next) => {
    const v = typeof next === 'function' ? next(view) : next;
    if (onMonthChange) onMonthChange(`${v.y}-${pad(v.m + 1)}`);
    else setSelfView(v);
  };

  const holidayByDate = useMemo(
    () => Object.fromEntries((settings.holidays || []).map((h) => [h.date, h])),
    [settings.holidays]
  );
  // Memoised: `settings.workWeek || []` would be a fresh array on every render,
  // so the grid below would rebuild each time and the memo would buy nothing.
  const workWeek = useMemo(() => settings.workWeek || [], [settings.workWeek]);
  const todayKey = ymd(today.getFullYear(), today.getMonth(), today.getDate());

  // The grid: leading blanks so the 1st sits under its weekday, then the days.
  const cells = useMemo(() => {
    const first = new Date(view.y, view.m, 1);
    const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
    const lead = gridDow(first.getDay());
    const out = Array.from({ length: lead }, () => null);
    for (let d = 1; d <= daysInMonth; d += 1) {
      const key = ymd(view.y, view.m, d);
      const jsDay = new Date(view.y, view.m, d).getDay();
      const holiday = holidayByDate[key];
      const isOff = !workWeek.includes(jsDay);
      out.push({
        d,
        key,
        holiday,
        isOff,
        isToday: key === todayKey,
        // A holiday on a weekly off is still a holiday, but it owes nothing
        // either way — worth saying, because it looks like a duplicate rule.
        redundant: !!holiday && isOff,
        // Whatever the caller knows about this day that the rules don't.
        over: overlay ? overlay[key] || null : null,
      });
    }
    return out;
  }, [view.y, view.m, holidayByDate, workWeek, todayKey, overlay]);

  // The month's shape, from the rules as they stand in the form.
  const summary = useMemo(() => {
    const days = cells.filter(Boolean);
    return {
      working: days.filter((c) => !c.isOff && !c.holiday).length,
      off: days.filter((c) => c.isOff).length,
      holidays: days.filter((c) => c.holiday && !c.isOff).length,
    };
  }, [cells]);

  const monthHolidays = useMemo(
    () => (settings.holidays || [])
      .filter((h) => h.date.startsWith(`${view.y}-${pad(view.m + 1)}`))
      .sort((a, b) => a.date.localeCompare(b.date)),
    [settings.holidays, view]
  );

  const step = (delta) => setView((v) => {
    const n = v.m + delta;
    return { y: v.y + Math.floor(n / 12), m: ((n % 12) + 12) % 12 };
  });

  // What this day IS, once the overlay has had its say. The overlay wins:
  // "Sick leave" is more use than "working day" on a day someone was off.
  const describe = (c) => {
    const when = `${weekdayName(c.key)} ${prettyDate(c.key)}`;
    if (c.over) return `${when} — ${c.over.label}${c.over.detail ? ` · ${c.over.detail}` : ''}`;
    if (c.holiday) {
      return `${when} — ${c.holiday.name}`
        + (c.redundant ? ' (already a weekly off)' : ' · holiday, owes no hours');
    }
    if (c.isOff) return `${when} — weekly off`;
    return `${when} — working day`;
  };

  // The cell's tone. An overlay tone replaces the rules-derived one entirely
  // rather than layering on top, so a day is never two colours at once.
  const toneOf = (c) => {
    if (c.over?.tone) return `is-${c.over.tone}`;
    if (c.holiday) return 'is-holiday';
    if (c.isOff) return 'is-off';
    return 'is-work';
  };

  return (
    <div className="att-cal">
      <div className="att-cal-head">
        <button type="button" className="att-cal-nav" onClick={() => step(-1)} aria-label="Previous month">‹</button>
        <span className="att-cal-title">{MONTHS[view.m]} {view.y}</span>
        <button type="button" className="att-cal-nav" onClick={() => step(1)} aria-label="Next month">›</button>
        <button
          type="button"
          className="att-cal-today"
          onClick={() => setView({ y: today.getFullYear(), m: today.getMonth() })}
        >
          Today
        </button>
      </div>

      <div className="att-cal-grid" role="grid" aria-label={`${MONTHS[view.m]} ${view.y}`}>
        {DOW_LABELS.map((l) => (
          <span key={l} className="att-cal-dow" role="columnheader">{l}</span>
        ))}

        {cells.map((c, i) => {
          if (!c) return <span key={`b${i}`} className="att-cal-blank" aria-hidden="true" />;
          const cls = [
            'att-cal-day',
            toneOf(c),
            c.isToday ? 'is-today' : '',
            // A second axis, not a fifth colour: "awaiting a decision" is
            // orthogonal to what kind of day it is, so it hatches the fill
            // rather than replacing it.
            c.over?.pending ? 'is-pending' : '',
          ].filter(Boolean).join(' ');

          return (
            <button
              key={c.key}
              type="button"
              className={cls}
              // Clicking a day loads it into the add-holiday row, so the
              // calendar is the way you add one rather than a picture of them.
              onClick={() => onPickDate?.(c.key, c.holiday?.name || '', c.over || null)}
              disabled={!onPickDate}
              aria-label={describe(c)}
            >
              <span className="att-cal-num">{c.d}</span>
              {(c.holiday || c.over?.dot) && <span className="att-cal-dot" aria-hidden="true" />}
              {/* The reason, on hover and on keyboard focus. A plain `title`
                  would work but waits ~1s and can't be styled — too slow for
                  scanning a month. aria-label above carries it for AT. */}
              <span className="att-cal-tip" role="presentation">
                {c.over
                  ? c.over.label
                  : c.holiday ? c.holiday.name : c.isOff ? 'Weekly off' : 'Working day'}
                <small>{c.over?.detail || prettyDate(c.key)}</small>
                {c.over?.detail && <small>{prettyDate(c.key)}</small>}
              </span>
            </button>
          );
        })}
      </div>

      <div className="att-cal-legend">
        {legendExtra || (<>
        <span><i className="att-key is-work" /> Working · {summary.working}</span>
        <span><i className="att-key is-off" /> Weekly off · {summary.off}</span>
        <span><i className="att-key is-holiday" /> Holiday · {summary.holidays}</span>
        </>)}
      </div>

      {showHolidayList && (
      <div className="att-cal-hols">
        <h5 className="att-cal-hols-h">Holidays in {MONTHS[view.m]} {view.y}</h5>
        {monthHolidays.length === 0 ? (
          <p className="att-empty">
            No holidays this month.
            {canWrite && ' Pick a date above to add one.'}
          </p>
        ) : (
          <ul className="att-cal-hol-list">
            {monthHolidays.map((h) => {
              const onOff = !workWeek.includes(jsDayOf(h.date));
              return (
                <li key={h.date} className="att-cal-hol">
                  <span className="att-cal-hol-date">
                    <b>{prettyDate(h.date)}</b>
                    <small>{weekdayName(h.date)}</small>
                  </span>
                  <span className="att-cal-hol-name">
                    {h.name}
                    {onOff && <em className="att-cal-hol-note">falls on a weekly off — owes nothing either way</em>}
                  </span>
                  {canWrite && (
                    <button
                      type="button"
                      className="att-cal-hol-x"
                      onClick={() => onRemoveHoliday?.(h.date)}
                      aria-label={`Remove ${h.name} on ${prettyDate(h.date)}`}
                    >
                      ✕
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      )}
    </div>
  );
}

export default WorkCalendar;
